import type {
  FullHeadMprSlicePixelRaster,
  FullHeadMprSliceViewOptionsView,
} from '../config';

export const hasVisibleFullHeadMprSliceViewOptions = (
  options: FullHeadMprSliceViewOptionsView,
) =>
  options.brightness !== 0 ||
  options.contrast !== 0 ||
  options.sharpness !== 0 ||
  options.invert;

export const applyFullHeadMprSliceViewOptionsToPixels = (
  { data, heightPx, widthPx }: FullHeadMprSlicePixelRaster,
  options: FullHeadMprSliceViewOptionsView,
): Uint8ClampedArray => {
  const { brightness, contrast, invert, sharpness } = options;
  const output = new Uint8ClampedArray(data.length);

  const sampleChannel = (x: number, y: number, channel: number) => {
    const clampedX = Math.min(Math.max(x, 0), widthPx - 1);
    const clampedY = Math.min(Math.max(y, 0), heightPx - 1);

    return data[(clampedY * widthPx + clampedX) * 4 + channel] / 255;
  };

  for (let y = 0; y < heightPx; y += 1) {
    for (let x = 0; x < widthPx; x += 1) {
      const pixelOffset = (y * widthPx + x) * 4;

      for (let channel = 0; channel < 3; channel += 1) {
        const center = data[pixelOffset + channel] / 255;
        let value = center;

        if (sharpness !== 0) {
          const convolved =
            center * 9 -
            sampleChannel(x - 1, y - 1, channel) -
            sampleChannel(x - 1, y, channel) -
            sampleChannel(x - 1, y + 1, channel) -
            sampleChannel(x, y - 1, channel) -
            sampleChannel(x, y + 1, channel) -
            sampleChannel(x + 1, y - 1, channel) -
            sampleChannel(x + 1, y, channel) -
            sampleChannel(x + 1, y + 1, channel);

          value = center + (convolved - center) * sharpness;
        }

        value += brightness;

        value =
          contrast > 0
            ? (value - 0.5) / (1 - contrast) + 0.5
            : (value - 0.5) * (1 + contrast) + 0.5;

        if (invert) {
          value = 1 - value;
        }

        output[pixelOffset + channel] = value * 255;
      }

      output[pixelOffset + 3] = data[pixelOffset + 3];
    }
  }

  return output;
};

export const applyFullHeadMprSliceViewOptionsToPngUrl = (
  pngUrl: string,
  options: FullHeadMprSliceViewOptionsView,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const context = canvas.getContext('2d');

        if (!context) {
          reject(new Error('2d context is not available'));

          return;
        }

        context.drawImage(image, 0, 0);

        const imageData = context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        );
        const adjusted = applyFullHeadMprSliceViewOptionsToPixels(
          {
            data: imageData.data,
            heightPx: canvas.height,
            widthPx: canvas.width,
          },
          options,
        );

        imageData.data.set(adjusted);
        context.putImageData(imageData, 0, 0);

        resolve(canvas.toDataURL('image/png'));
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = () => reject(new Error('failed to decode tile png'));
    image.src = pngUrl;
  });
